import { toHttpError, toNetworkError } from './apiError';
import {
  accessTokenNeedsRefresh,
  refreshTokenIsUsable,
  type SessionTokens,
  type StoredSession,
  type TokenStorage,
} from './tokenStorage';

/**
 * Client HTTP unique du backoffice.
 *
 * Une seule porte vers l'API, pour une raison precise : le rafraichissement du
 * jeton doit etre **serialise**. Sans point de passage unique, huit requetes
 * lancees en parallele par un tableau qui recoit un 401 declencheraient huit
 * rafraichissements concurrents ; le backend faisant tourner le jeton de
 * rafraichissement a chaque appel, sept d'entre eux echoueraient et
 * deconnecteraient l'utilisateur au milieu d'un ecran qui s'affichait
 * correctement. La promesse en cours est donc partagee (`pendingRefresh`).
 *
 * Le client couvre deux cas, pas un :
 *
 * - **rafraichissement proactif** avant expiration connue, evitant le 401 ;
 * - **recouvrement reactif** apres un 401 inattendu — revocation cote serveur,
 *   horloge du poste decalee — avec **un seul** rejeu.
 *
 * Le second est celui qui manque cote mobile (`forceRefresh` n'y est appele par
 * aucun code, CLAUDE.md §3) : il est ici la regle, pas une option.
 */

export interface RequestOptions {
  readonly method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  readonly path: string;
  readonly query?: Readonly<Record<string, string | number | undefined>>;
  readonly body?: unknown;
  /** Requete d'authentification : aucun jeton pose, aucun rejeu tente. */
  readonly anonymous?: boolean;
  /**
   * La reponse est un **fichier** (export CSV) : `request` rend alors un
   * `DownloadedFile`, octets intacts et en-tetes lisibles, au lieu d'un corps
   * relu comme du texte — `Response.text()` retire un BOM UTF-8, et le BOM est
   * ce qui fait reconnaitre l'encodage a Excel. Les erreurs restent du JSON.
   */
  readonly asFile?: boolean;
}

/** Fichier rendu par une requete `asFile`. */
export interface DownloadedFile {
  readonly blob: Blob;
  readonly headers: Headers;
}

export interface HttpClient {
  /** URL de base resolue au demarrage. Exposee pour la detection Hydra, qui a son propre client. */
  readonly apiUrl: string;
  request<T>(options: RequestOptions): Promise<T>;
  /** Session courante, ou `null`. Lue par `authProvider`. */
  session(): StoredSession | null;
  /** Enregistre la session issue de `/auth/login` ou `/auth/register`. */
  setSession(session: StoredSession): void;
  clearSession(): void;
}

/** Erreur levee quand plus aucune session valide ne peut etre obtenue. */
export class SessionExpiredError extends Error {
  constructor() {
    super('Votre session a expiré. Reconnectez-vous.');
    this.name = 'SessionExpiredError';
  }
}

interface HttpClientDependencies {
  readonly apiUrl: string;
  readonly storage: TokenStorage;
  readonly fetchImpl?: typeof fetch;
  readonly now?: () => Date;
}

/** Corps attendu de `/auth/login`, `/auth/register` et `/auth/refresh`. */
const isSessionResponse = (value: unknown): value is StoredSession => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const record = value as Record<string, unknown>;

  return typeof record.tokens === 'object' && record.tokens !== null && typeof record.user === 'object';
};

const buildUrl = (
  apiUrl: string,
  path: string,
  query: RequestOptions['query'],
): string => {
  const suffix = path.startsWith('/') ? path : `/${path}`;
  const search = new URLSearchParams();

  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== '') {
      search.set(key, String(value));
    }
  }

  const queryString = search.toString();

  return queryString === '' ? `${apiUrl}${suffix}` : `${apiUrl}${suffix}?${queryString}`;
};

/**
 * Lit le corps sans supposer qu'il est du JSON.
 *
 * Plusieurs routes repondent 204 sans corps (`/auth/logout`,
 * `/auth/reset-password`), et une erreur de passerelle peut renvoyer du HTML.
 * `response.json()` leverait dans les deux cas, masquant le vrai statut.
 */
const readBody = async (response: Response): Promise<unknown> => {
  const text = await response.text();

  if (text === '') {
    return null;
  }

  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
};

export const createHttpClient = ({
  apiUrl,
  storage,
  fetchImpl = globalThis.fetch.bind(globalThis),
  now = () => new Date(),
}: HttpClientDependencies): HttpClient => {
  // Copie memoire de la session : elle survit a un `sessionStorage`
  // indisponible, cas dans lequel l'ecriture est silencieusement perdue.
  let cached: StoredSession | null = storage.read();

  /** Rafraichissement en cours, partage par tous les appelants. */
  let pendingRefresh: Promise<SessionTokens> | null = null;

  const session = (): StoredSession | null => cached;

  const setSession = (next: StoredSession): void => {
    cached = next;
    storage.write(next);
  };

  const clearSession = (): void => {
    cached = null;
    pendingRefresh = null;
    storage.clear();
  };

  /** Appel brut, sans jeton ni rejeu. Sert aussi au rafraichissement lui-meme. */
  const send = async (
    options: RequestOptions,
    accessToken: string | null,
  ): Promise<{ status: number; body: unknown }> => {
    const headers: Record<string, string> = { Accept: options.asFile === true ? '*/*' : 'application/json' };

    if (options.body !== undefined) {
      headers['Content-Type'] = 'application/json';
    }

    if (accessToken !== null) {
      headers.Authorization = `Bearer ${accessToken}`;
    }

    let response: Response;

    try {
      response = await fetchImpl(buildUrl(apiUrl, options.path, options.query), {
        method: options.method ?? 'GET',
        headers,
        ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
      });
    } catch (cause) {
      // `fetch` ne rejette que sur un echec de transport : API arretee, DNS
      // muet, ou prevol CORS refuse. Le distinguer d'une erreur HTTP est ce qui
      // permet d'afficher un message actionnable plutot que « échec ».
      throw toNetworkError(cause);
    }

    // Un fichier n'est lu en octets que s'il est reussi : une erreur reste le
    // JSON `{error: {code}}` que `toHttpError` sait traduire.
    if (options.asFile === true && response.ok) {
      return { status: response.status, body: { blob: await response.blob(), headers: response.headers } };
    }

    return { status: response.status, body: await readBody(response) };
  };

  /**
   * Echange le jeton de rafraichissement contre un nouveau couple.
   *
   * Le resultat est memorise dans `pendingRefresh` pour la duree de l'appel :
   * tout appelant concurrent attend la meme promesse au lieu d'en lancer une
   * autre.
   */
  const refresh = async (): Promise<SessionTokens> => {
    const current = cached;

    if (current === null || !refreshTokenIsUsable(current.tokens, now())) {
      clearSession();
      throw new SessionExpiredError();
    }

    const { status, body } = await send(
      {
        method: 'POST',
        path: '/auth/refresh',
        body: { refreshToken: current.tokens.refreshToken },
      },
      null,
    );

    if (status < 200 || status >= 300 || !isSessionResponse(body)) {
      clearSession();
      throw new SessionExpiredError();
    }

    setSession(body);

    return body.tokens;
  };

  const refreshOnce = (): Promise<SessionTokens> => {
    pendingRefresh ??= refresh().finally(() => {
      pendingRefresh = null;
    });

    return pendingRefresh;
  };

  /** Jeton a poser, renouvele d'avance s'il est sur le point d'expirer. */
  const accessToken = async (): Promise<string> => {
    const current = cached;

    if (current === null) {
      throw new SessionExpiredError();
    }

    if (!accessTokenNeedsRefresh(current.tokens, now())) {
      return current.tokens.accessToken;
    }

    return (await refreshOnce()).accessToken;
  };

  const request = async <T>(options: RequestOptions): Promise<T> => {
    if (options.anonymous === true) {
      const { status, body } = await send(options, null);

      if (status < 200 || status >= 300) {
        throw toHttpError(status, body);
      }

      return body as T;
    }

    const first = await send(options, await accessToken());

    if (first.status !== 401) {
      if (first.status < 200 || first.status >= 300) {
        throw toHttpError(first.status, first.body);
      }

      return first.body as T;
    }

    // 401 inattendu : le jeton etait valide selon sa date d'expiration mais le
    // serveur l'a refuse. Un seul rejeu, jamais une boucle — deux 401 de suite
    // signifient que la session est reellement perdue.
    const retried = await send(options, (await refreshOnce()).accessToken);

    if (retried.status < 200 || retried.status >= 300) {
      if (retried.status === 401) {
        clearSession();
        throw new SessionExpiredError();
      }

      throw toHttpError(retried.status, retried.body);
    }

    return retried.body as T;
  };

  return { apiUrl, request, session, setSession, clearSession };
};
